import React from 'react';
import Distance from '../../../../styled/Distance';
import Text from '../../../../styled/Text';
import { useGetSimilarVariantsInStock } from '../../../../api/article/similarInStock';
import useContext from '../../../../hooks/useContext';
import { StoreContext } from '../../../../providers/store';
import { ProductContext } from '../providers/ProductProvider';
import { ConfigurationContext } from '../providers/ConfigurationProvider';
import StoreSelectionBox from '../../../../components-legacy/StoreSelectionBox';
import { H3 } from '../../../../styled/Heading';
import Loader from '../../../../styled/Loader';
import VariantInStockOption from './VariantInStockOption';
import { EntityType } from '../../../../../@types/product.enum';
import useHistory from '../../../../Router/useHistory';

const userStorePlaceholderText = 'Välj butik för att se tillgängliga utföranden i butik.';
const selectStoreDescription = `Utforska tillgängliga utföranden i butikslagret genom att välja din butik nedan.`;

const noVariantsPlaceholderText = (storeName: string) =>
  `Inga andra tillgängliga utföranden i butikslagret hos ${storeName}. 
  Välj en annan butik för att se andra tillgängliga utföranden.`;

const isConfigurationVariant = (variant: IOptionResponse) =>
  variant.entityType === EntityType.configuration;

interface IVariantsList {
  handleChangeStore: () => void;
  handleSelectVariant: () => void;
}

export default function VariantsList({ handleChangeStore, handleSelectVariant }: IVariantsList) {
  const { userStore, loading: loadingUserStore } = useContext(StoreContext);
  const { groupId } = useContext(ProductContext);
  const { selectOption } = useContext(ConfigurationContext);
  const { push } = useHistory();

  const { data: variants, isLoading: loadingVariants } = useGetSimilarVariantsInStock(
    groupId,
    userStore?.storeId,
  );

  const showLoader = loadingVariants || loadingUserStore;
  const showUserStorePlaceholderText = !showLoader && !userStore;
  const showNoVariantsPlaceholderText = !showLoader && userStore && !variants?.length;
  const showVariants = !showLoader && userStore && variants;

  const handleVariantClick = (variant: IOptionResponse) => {
    if (isConfigurationVariant(variant)) {
      selectOption({ variant });
    } else {
      push({ search: `?id=${variant.m3Id}` });
    }

    handleSelectVariant();
  };

  return (
    <>
      <H3 className="alt-font">Utföranden i butikslager</H3>
      <Distance size="4" />

      {showLoader && (
        <>
          <Distance size="8" />
          <Loader />
        </>
      )}

      {!showLoader && (
        <>
          <StoreSelectionBox
            description={selectStoreDescription}
            onActionClick={handleChangeStore}
          />
          <Distance size="4" />

          <Text weight="medium" size={['standard', 'large']}>
            Tillgängliga utföranden i butikslagret
          </Text>

          {(showUserStorePlaceholderText || showNoVariantsPlaceholderText) && (
            <>
              <Distance size="1" />
              <Text size={['small', 'standard']}>
                {showUserStorePlaceholderText && userStorePlaceholderText}
                {showNoVariantsPlaceholderText && noVariantsPlaceholderText(userStore.storeName)}
              </Text>
            </>
          )}

          {showVariants && (
            <>
              <Distance size="3" />
              {variants.map((variant) => (
                <React.Fragment key={variant.id}>
                  <VariantInStockOption
                    key={variant.variantId}
                    variant={variant}
                    isConfigurationProduct={isConfigurationVariant(variant)}
                    onSelect={() => handleVariantClick(variant)}
                  />
                  <Distance line size="2" />
                </React.Fragment>
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}
